/**
 * Trip history: local log merged with the server copy, per signed-in driver.
 *
 * A trip row used to be one line - distance, duration, vehicle - which was too
 * little to be worth keeping. Each trip now expands into what actually
 * happened: average and top speed, how long was spent moving versus stopped,
 * fuel estimate, how the drive compared with the estimate at departure, and a
 * segment-by-segment breakdown of the route with the road number and speed for
 * each few minutes of the drive.
 */

import { listTrips, deleteTrip } from '../core/db';
import { fetchTrips, syncTrip } from '../auth/api';
import type { Trip, TripSegment } from '../core/types';
import { formatClock, formatDateTime, formatDistance, formatDuration } from '../core/format';
import { store } from '../core/store';
import { t } from '../core/i18n';

export class TripsPanel {
  readonly el: HTMLElement;
  private expanded = new Set<string>();
  private trips: Trip[] | null = null;

  constructor(
    parent: HTMLElement,
    private onClose: () => void,
    private onNavigateAgain: (trip: Trip) => void,
    /** Draw this trip's recorded track on the map, speed-coloured. */
    private onShowMap: (trip: Trip) => void,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'panel panel--trips';
    this.el.hidden = true;
    parent.appendChild(this.el);
  }

  async open(): Promise<void> {
    this.el.hidden = false;
    this.trips = null;
    this.renderList();
    const local = await listTrips();
    let merged = local;
    try {
      const server = (await fetchTrips()).trips;
      const seen = new Set(local.map((x) => x.id));
      merged = [...local, ...server.filter((x) => !seen.has(x.id))].sort(
        (a, b) => b.startedAt - a.startedAt,
      );

      /*
       * Re-upload trips the server holds only a stripped copy of.
       *
       * Until this was fixed the API discarded segments, speeds, moving time
       * and fuel on upload, so history opened on any other device had nothing
       * in it. The phone that recorded the drive still has the full record in
       * IndexedDB, so pushing those back fills the gap in place rather than
       * writing the detail off as lost.
       */
      const thin = new Map(server.map((x) => [x.id, x]));
      const toResend = local.filter((l) => {
        const remote = thin.get(l.id);
        if (!remote) return false;
        const localHas = (l.segments?.length ?? 0) > 0 || l.avgSpeed != null;
        const remoteHas = (remote.segments?.length ?? 0) > 0 || remote.avgSpeed != null;
        return localHas && !remoteHas;
      });
      // Bounded, and entirely best-effort: this is a repair, not a feature.
      for (const trip of toResend.slice(0, 25)) {
        void syncTrip(trip).catch(() => {});
      }
    } catch {
      /* offline: local only */
    }
    this.trips = merged;
    if (!this.el.hidden) this.renderList();
  }

  close(): void {
    this.el.hidden = true;
  }

  private renderList(): void {
    const units = store.get().settings.units;
    const trips = this.trips;
    const body =
      trips === null
        ? `<div class="panel__hint">${t('trips.loading')}</div>`
        : trips.length === 0
          ? `<div class="panel__hint">${t('trips.empty')}</div>`
          : trips.map((tr, i) => this.tripHtml(tr, i, units)).join('');

    this.el.innerHTML = `
      <div class="panel__bar">
        <button class="icon-btn" data-act="close" aria-label="Back">
          <svg viewBox="0 0 24 24"><path d="M15 4 L7 12 L15 20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <h2>${t('trips.title')}</h2>
      </div>
      <div class="panel__scroll">${body}</div>`;

    this.el.querySelector('[data-act=close]')!.addEventListener('click', () => this.onClose());
    if (!trips) return;

    this.el.querySelectorAll<HTMLButtonElement>('[data-again]').forEach((b) =>
      b.addEventListener('click', () => this.onNavigateAgain(trips[Number(b.dataset.again)])),
    );
    this.el.querySelectorAll<HTMLButtonElement>('[data-map]').forEach((b) =>
      b.addEventListener('click', () => this.onShowMap(trips[Number(b.dataset.map)])),
    );
    this.el.querySelectorAll<HTMLButtonElement>('[data-del]').forEach((b) =>
      b.addEventListener('click', async () => {
        const tr = trips[Number(b.dataset.del)];
        await deleteTrip(tr.id);
        void this.open();
      }),
    );
    const toggle = (id: string) => {
      if (this.expanded.has(id)) this.expanded.delete(id);
      else this.expanded.add(id);
      this.renderList();
    };

    this.el.querySelectorAll<HTMLButtonElement>('[data-expand]').forEach((b) =>
      b.addEventListener('click', () => toggle(b.dataset.expand!)),
    );

    /*
     * The whole card opens it, not just the small "details" button.
     *
     * Reported as "clicking one does nothing": the only click targets were
     * three small buttons at the bottom of the card, and the obvious thing to
     * click - the trip itself - was inert. Clicks inside the action row are
     * left alone so Again and Delete still do their own jobs.
     */
    this.el.querySelectorAll<HTMLElement>('[data-card]').forEach((card) => {
      card.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.trip__actions')) return;
        toggle(card.dataset.card!);
      });
      card.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        toggle(card.dataset.card!);
      });
    });
  }

  private tripHtml(tr: Trip, i: number, units: 'metric' | 'imperial'): string {
    const open = this.expanded.has(tr.id);
    const speedUnit = units === 'imperial' ? 'mph' : 'km/h';
    const conv = (kmh: number) => (units === 'imperial' ? Math.round(kmh / 1.609344) : kmh);

    const stoppedSeconds =
      tr.movingSeconds != null ? Math.max(0, tr.duration - tr.movingSeconds) : null;

    // How the drive compared with what the router promised at departure.
    let vsEstimate = '';
    if (tr.predictedDuration && tr.duration) {
      const diff = tr.duration - tr.predictedDuration;
      const sign = diff >= 0 ? '+' : '-';
      const cls = diff > 120 ? 'is-slower' : diff < -120 ? 'is-faster' : '';
      vsEstimate = `<span class="trip-stat ${cls}">${t('trips.vsEstimate')}
        <strong>${sign}${formatDuration(Math.abs(diff))}</strong></span>`;
    }

    const stats = [
      tr.avgSpeed
        ? `<span class="trip-stat">${t('trips.avgSpeed')} <strong>${conv(tr.avgSpeed)} ${speedUnit}</strong></span>`
        : '',
      tr.maxSpeed
        ? `<span class="trip-stat">${t('trips.maxSpeed')} <strong>${conv(tr.maxSpeed)} ${speedUnit}</strong></span>`
        : '',
      tr.movingSeconds != null
        ? `<span class="trip-stat">${t('trips.moving')} <strong>${formatDuration(tr.movingSeconds)}</strong></span>`
        : '',
      stoppedSeconds != null && stoppedSeconds > 30
        ? `<span class="trip-stat">${t('trips.stopped')} <strong>${formatDuration(stoppedSeconds)}</strong></span>`
        : '',
      tr.fuelEstimate
        ? `<span class="trip-stat">${t('trips.fuel')} <strong>${tr.fuelEstimate} L</strong></span>`
        : '',
      tr.trafficSignals
        ? `<span class="trip-stat">${t('stats.signals', { n: tr.trafficSignals })}</span>`
        : '',
      tr.reroutes ? `<span class="trip-stat">${t('trips.reroutes', { n: tr.reroutes })}</span>` : '',
      vsEstimate,
    ]
      .filter(Boolean)
      .join('');

    const segments = (tr.segments ?? []).length
      ? `<div class="trip-segments">
           <div class="trip-segments__title">${t('trips.segments')}</div>
           ${tr.segments!.map((s) => this.segmentHtml(s, units, speedUnit, conv)).join('')}
         </div>`
      : '';

    return `
      <div class="trip ${open ? 'is-open' : ''}" data-i="${i}" data-card="${escapeHtml(tr.id)}"
           role="button" tabindex="0" aria-expanded="${open}">
        <div class="trip__head">
          <strong>${escapeHtml(tr.destinationName)}</strong>
          <span>${formatDateTime(tr.startedAt)}</span>
        </div>
        <div class="trip__meta">
          ${formatDistance(tr.distance, units)} · ${formatDuration(tr.duration)} · ${escapeHtml(tr.vehicleId)}
        </div>
        ${
          open
            ? stats || segments
              ? `<div class="trip-stats">${stats}</div>${segments}`
              : `<div class="trip-stats trip-stats--empty">${t('trips.noDetail')}</div>`
            : ''
        }
        <div class="trip__actions">
          <button class="btn btn--small btn--ghost" data-expand="${escapeHtml(tr.id)}">
            ${open ? '▴' : '▾'} ${t('trips.details')}
          </button>
          ${
            (tr.track?.length ?? 0) >= 2
              ? `<button class="btn btn--small btn--ghost" data-map="${i}">${t('trips.map')}</button>`
              : ''
          }
          <button class="btn btn--small btn--ghost" data-again="${i}">${t('trips.again')}</button>
          <button class="btn btn--small btn--ghost" data-del="${i}">${t('trips.delete')}</button>
        </div>
      </div>`;
  }

  private segmentHtml(
    s: TripSegment,
    units: 'metric' | 'imperial',
    speedUnit: string,
    conv: (kmh: number) => number,
  ): string {
    const clock = formatClock;
    return `
      <div class="trip-seg">
        <span class="trip-seg__time">${clock(s.startedAt)}</span>
        <span class="trip-seg__road">${escapeHtml(s.road || t('trips.unknownRoad'))}</span>
        <span class="trip-seg__num">${formatDistance(s.distance, units)}</span>
        <span class="trip-seg__num">${conv(s.avgSpeed)}<small>/${conv(s.maxSpeed)} ${speedUnit}</small></span>
        ${
          s.stoppedSeconds > 20
            ? `<span class="trip-seg__stop">■ ${formatDuration(s.stoppedSeconds)}</span>`
            : '<span class="trip-seg__stop"></span>'
        }
      </div>`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
